import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  useTheme,
  Zoom,
  Fab,
  Popper,
  ClickAwayListener,
  Grow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { 
  Send, 
  SmartToy, 
  Person, 
  Close,
  ContentCopy,
  DeleteOutline,
  Chat as ChatIcon,
  Image as ImageIcon,
  AddPhotoAlternate,
  Check
} from '@mui/icons-material';
import useChatBot from '../hooks/useChatBot';

// Suggested prompts that users can click on
const SUGGESTED_PROMPTS = [
  "Analyze my win rate trend",
  "What trading patterns led to my losses?",
  "Suggest risk management improvements",
  "Compare my strategy performance",
  "When are my trades most profitable?",
  "Identify my best trading setups"
];

const FloatingChatBot = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [createTradeDialogOpen, setCreateTradeDialogOpen] = useState(false);
  const [selectedTradeData, setSelectedTradeData] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const { 
    messages, 
    loading, 
    error, 
    imageFile,
    imagePreview,
    sendMessage, 
    clearChat,
    handleImageUpload,
    sendImageForAnalysis,
    createTradeFromAnalysis
  } = useChatBot();
  
  const messagesEndRef = useRef(null);
  const anchorRef = useRef(null);
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // Colors based on theme
  const botBgColor = isDarkMode ? 'grey.800' : 'grey.100';
  const botTextColor = isDarkMode ? 'grey.100' : 'text.primary';
  const userBgColor = 'primary.main';
  const userTextColor = 'white';
  const paperBgColor = isDarkMode ? 'background.paper' : 'white';

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async () => {
    if (input.trim()) {
      await sendMessage(input);
      setInput('');
      setShowSuggestions(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  // Handle paste events (Ctrl+V) for images
  useEffect(() => {
    const handlePaste = async (e) => {
      if (!open) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            e.preventDefault();
            const success = await handleImageUpload(file);
            if (success) {
              setImageDialogOpen(true);
            }
            break;
          }
        }
      }
    };
    
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [open, handleImageUpload]);

  const handleSuggestedPrompt = (prompt) => {
    sendMessage(prompt);
    setShowSuggestions(false);
  };

  const handleCopyMessage = (text) => {
    navigator.clipboard.writeText(text);
  };

  const toggleChat = () => {
    setOpen(prev => !prev);
  };

  const handleClickAway = () => {
    // Don't close chat if dialogs are open
    if (!imageDialogOpen && !createTradeDialogOpen) {
      setOpen(false);
    }
  };
  
  const handleImageClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const success = await handleImageUpload(file);
      if (success) {
        setImageDialogOpen(true);
      }
    }
  };
  
  const handleImageAnalysis = () => {
    // Close dialog first
    setImageDialogOpen(false);
    
    // Keep chat open and analyze image
    // Don't change the open state since it's already open
    sendImageForAnalysis();
  };
  
  const handleCreateTrade = (tradeData) => {
    setSelectedTradeData(tradeData);
    setCreateTradeDialogOpen(true);
  };
  
  const confirmCreateTrade = () => {
    createTradeFromAnalysis(selectedTradeData);
    setCreateTradeDialogOpen(false);
    setSelectedTradeData(null);
  };

  // Format message text with basic formatting
  const formatMessage = (text) => {
    // Split by new lines and map each line
    return text.split('\n').map((line, i) => (
      <Typography key={i} variant="body2" sx={{ mb: line.trim() === '' ? 0.5 : 0 }}>
        {line}
      </Typography>
    ));
  };

  return (
    <>
      <Fab
        color="primary"
        aria-label="chat"
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000
        }}
        ref={anchorRef}
        onClick={toggleChat}
      >
        {open ? <Close /> : <ChatIcon />}
      </Fab>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="top-end"
        transition
        disablePortal
        style={{ zIndex: 1050 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: 'bottom right' }}
          >
            <div>
              <ClickAwayListener onClickAway={handleClickAway}>
                <Paper 
                  elevation={6} 
                  sx={{ 
                    width: 400,
                    height: 600,
                    display: 'flex', 
                    flexDirection: 'column',
                    bgcolor: paperBgColor,
                    borderRadius: 2,
                    overflow: 'hidden',
                    mb: 2,
                    boxShadow: theme.shadows[10]
                  }}
                >
                  {/* Header */}
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderBottom: 1, 
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: theme.palette.primary.main,
                      color: 'white'
                    }}
                  >
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SmartToy fontSize="small" />
                      Trading Assistant
                    </Typography>
                    <Box>
                      <Tooltip title="Clear chat">
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            clearChat();
                            setShowSuggestions(true);
                          }}
                          sx={{ color: 'white' }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Messages */}
                  <List sx={{ flex: 1, overflow: 'auto', p: 1, bgcolor: isDarkMode ? 'background.default' : 'grey.50' }}>
                    {messages.length === 0 && !loading && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%',
                        p: 2,
                        opacity: 0.8
                      }}>
                        <SmartToy sx={{ fontSize: 40, mb: 2, color: 'primary.main' }} />
                        <Typography variant="body1" sx={{ mb: 1, textAlign: 'center' }}>
                          How can I help with your trading today?
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                          Ask me about your performance, strategies, or for trading advice.
                        </Typography>
                      </Box>
                    )}

                    {messages.map((msg) => (
                      <ListItem 
                        key={msg.id} 
                        sx={{ 
                          flexDirection: 'column', 
                          alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                          mb: 1.5,
                          px: 0.5
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '85%',
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: msg.sender === 'user' ? userBgColor : botBgColor,
                            color: msg.sender === 'user' ? userTextColor : botTextColor,
                            boxShadow: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            {msg.sender === 'user' ? <Person fontSize="small" /> : <SmartToy fontSize="small" />}
                            <Typography variant="caption">
                              {msg.sender === 'user' ? 'You' : 'AI'}
                            </Typography>
                            
                            {msg.sender === 'ai' && (
                              <Tooltip title="Copy response">
                                <IconButton 
                                  size="small" 
                                  sx={{ ml: 'auto', color: isDarkMode ? 'grey.400' : 'grey.600', p: 0.2 }}
                                  onClick={() => handleCopyMessage(msg.text)}
                                >
                                  <ContentCopy fontSize="small" sx={{ fontSize: '0.8rem' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          
                          <Box sx={{ 
                            '& p': { m: 0, mb: 0.5 },
                            whiteSpace: 'pre-wrap'
                          }}>
                            {formatMessage(msg.text)}
                            {msg.tradeData && (
                              <Button
                                variant="contained"
                                size="small"
                                color="primary"
                                startIcon={<Check />}
                                onClick={() => handleCreateTrade(msg.tradeData)}
                                sx={{ mt: 1 }}
                              >
                                Create Trade
                              </Button>
                            )}
                            {msg.hasImage && (
                              <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  [Image analyzed]
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                    ))}
                    
                    {loading && (
                      <ListItem sx={{ justifyContent: 'flex-start' }}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: botBgColor,
                            color: botTextColor,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <CircularProgress size={16} color="inherit" />
                          <Typography variant="body2">Thinking...</Typography>
                        </Box>
                      </ListItem>
                    )}
                    <div ref={messagesEndRef} />
                  </List>

                  {/* Suggested prompts */}
                  {showSuggestions && messages.length === 0 && (
                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Suggested questions:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {SUGGESTED_PROMPTS.map((prompt, index) => (
                          <Chip
                            key={index}
                            label={prompt}
                            onClick={() => handleSuggestedPrompt(prompt)}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Error message */}
                  {error && (
                    <Alert 
                      severity="error" 
                      sx={{ m: 1 }}
                    >
                      {error}
                    </Alert>
                  )}

                  {/* Input area */}
                  <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Tooltip title="Upload chart image (or paste with Ctrl+V)">
                          <IconButton 
                            onClick={handleImageClick} 
                            disabled={loading}
                            color="primary"
                            size="small"
                          >
                            <AddPhotoAlternate />
                          </IconButton>
                        </Tooltip>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Ask or paste image (Ctrl+V)..."
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyPress={handleKeyPress}
                          disabled={loading}
                          multiline
                          maxRows={3}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 3,
                              bgcolor: isDarkMode ? 'background.default' : 'white'
                            }
                          }}
                        />
                      </Box>
                      <IconButton 
                        onClick={handleSend} 
                        disabled={loading || !input.trim()}
                        color="primary"
                        sx={{ 
                          bgcolor: input.trim() ? 'primary.main' : 'grey.300', 
                          color: 'white',
                          '&:hover': {
                            bgcolor: input.trim() ? 'primary.dark' : 'grey.300',
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'grey.300',
                            color: 'grey.500'
                          }
                        }}
                      >
                        <Send />
                      </IconButton>
                    </Box>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      ref={fileInputRef}
                      onChange={handleFileChange}
                    />
                  </Box>
                </Paper>
              </ClickAwayListener>
            </div>
          </Grow>
        )}
      </Popper>
      {/* Image Upload Dialog - Using disableEnforceFocus and disableAutoFocus to prevent closing the chat */}
      <Dialog 
        open={imageDialogOpen} 
        onClose={() => setImageDialogOpen(false)}
        disableEnforceFocus
        disableAutoFocus
        container={document.body} // Render in a different container
        style={{ position: 'absolute' }} // Ensure it doesn't affect other components
        BackdropProps={{ style: { position: 'absolute' } }} // Prevent backdrop from closing chat
      >
        <DialogTitle>Analyze Chart Image</DialogTitle>
        <DialogContent>
          {imagePreview && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <img 
                src={imagePreview} 
                alt="Chart to analyze" 
                style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }} 
              />
              <Typography variant="body2">
                Would you like me to analyze this chart and suggest a trade setup?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleImageAnalysis}
            startIcon={<SmartToy />}
          >
            Analyze Chart
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Trade Dialog */}
      <Dialog open={createTradeDialogOpen} onClose={() => setCreateTradeDialogOpen(false)}>
        <DialogTitle>Create Trade</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to create a trade with the following details?
          </Typography>
          {selectedTradeData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2"><strong>Symbol:</strong> {selectedTradeData.symbol || 'Not specified'}</Typography>
              <Typography variant="body2"><strong>Direction:</strong> {selectedTradeData.direction || 'Not specified'}</Typography>
              <Typography variant="body2"><strong>Entry Price:</strong> {selectedTradeData.entryPrice || 'Not specified'}</Typography>
              <Typography variant="body2"><strong>Stop Loss:</strong> {selectedTradeData.stopLoss || 'Not specified'}</Typography>
              <Typography variant="body2"><strong>Take Profit:</strong> {selectedTradeData.exitPrice || 'Not specified'}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTradeDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={confirmCreateTrade}
          >
            Create Trade
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FloatingChatBot;